import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Minion, Tower, JungleCamp, SimChampion, Lane, LanePath, GRID_RES, CELL_SIZE } from './simulationTypes';
import { campImageUrl } from './jungleCampData';
import './SimulatorMap.css';

/* ══════════════════ Constants ══════════════════ */
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.15;

/** Camp leash range in game units (global for all camps) */
const CAMP_LEASH_RANGE = 600;
/** Leash range as % of the map (diameter / GRID_RES * 100) */
const CAMP_LEASH_PCT = ((CAMP_LEASH_RANGE / CELL_SIZE) * 2 / GRID_RES) * 100; // ~7.5%

/* ══════════════════ Minion drawing helpers ══════════════════ */
const TEAM_COLOR: Record<string, string> = {
  blue: '#4a9eff',
  red: '#ff6b6b',
};
const MINION_RADIUS: Record<string, number> = {
  melee: 3,
  caster: 2.5,
  siege: 4,
  super: 5,
};

function drawMinion(ctx: CanvasRenderingContext2D, m: Minion): void {
  const r = MINION_RADIUS[m.type] ?? 3;
  const color = TEAM_COLOR[m.team] ?? '#ccc';

  // Body circle
  ctx.beginPath();
  ctx.arc(m.x, m.y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // Darker outline
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 0.6;
  ctx.stroke();

  // Health bar (only if damaged)
  if (m.health < m.maxHealth) {
    const barW = r * 2.5;
    const barH = 1.4;
    const barX = m.x - barW / 2;
    const barY = m.y - r - 3;
    const pct = Math.max(0, m.health / m.maxHealth);

    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(barX, barY, barW, barH);
    // Foreground
    const hue = pct * 120; // 0=red, 120=green
    ctx.fillStyle = `hsl(${hue}, 90%, 45%)`;
    ctx.fillRect(barX, barY, barW * pct, barH);
  }
}

/* ══════════════════ Tower drawing helpers ══════════════════ */

const TOWER_TEAM_COLOR: Record<string, string> = {
  blue: '#3388ff',
  red: '#ff4444',
};
const TOWER_TEAM_RANGE_COLOR: Record<string, string> = {
  blue: 'rgba(51, 136, 255, 0.08)',
  red: 'rgba(255, 68, 68, 0.08)',
};
const TOWER_TEAM_RANGE_STROKE: Record<string, string> = {
  blue: 'rgba(51, 136, 255, 0.25)',
  red: 'rgba(255, 68, 68, 0.25)',
};

const TOWER_RADIUS: Record<string, number> = {
  outer: 5,
  inner: 5,
  inhib: 6,
  nexus: 5,
};

function drawTower(ctx: CanvasRenderingContext2D, t: Tower, showRange: boolean): void {
  const r = TOWER_RADIUS[t.tier] ?? 5;
  const color = TOWER_TEAM_COLOR[t.team] ?? '#888';

  // Range circle (drawn underneath)
  if (showRange) {
    ctx.beginPath();
    ctx.arc(t.gx, t.gy, t.stats.attackRange, 0, Math.PI * 2);
    ctx.fillStyle = TOWER_TEAM_RANGE_COLOR[t.team] ?? 'rgba(128,128,128,0.08)';
    ctx.fill();
    ctx.strokeStyle = TOWER_TEAM_RANGE_STROKE[t.team] ?? 'rgba(128,128,128,0.2)';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Tower body — filled square with diamond shape
  ctx.save();
  ctx.translate(t.gx, t.gy);
  ctx.rotate(Math.PI / 4); // 45° for diamond
  ctx.fillStyle = color;
  ctx.fillRect(-r / 1.4, -r / 1.4, r * 1.4, r * 1.4);
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 0.8;
  ctx.strokeRect(-r / 1.4, -r / 1.4, r * 1.4, r * 1.4);
  ctx.restore();

  // Tier indicator (small inner mark)
  if (t.tier === 'inhib' || t.tier === 'nexus') {
    ctx.beginPath();
    ctx.arc(t.gx, t.gy, r * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fill();
  }

  // Health bar (always show for towers)
  const barW = r * 3;
  const barH = 2;
  const barX = t.gx - barW / 2;
  const barY = t.gy - r - 4;
  const pct = Math.max(0, t.health / t.maxHealth);

  // Background
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.fillRect(barX, barY, barW, barH);
  // Foreground
  if (pct < 0.3) {
    ctx.fillStyle = '#ff3333';
  } else if (pct < 0.6) {
    ctx.fillStyle = '#ffaa33';
  } else {
    ctx.fillStyle = color;
  }
  ctx.fillRect(barX, barY, barW * pct, barH);
}

function drawDestroyedTower(ctx: CanvasRenderingContext2D, t: Tower): void {
  const r = (TOWER_RADIUS[t.tier] ?? 5) * 0.8;
  ctx.save();
  ctx.globalAlpha = 0.25;
  ctx.translate(t.gx, t.gy);
  ctx.rotate(Math.PI / 4);
  ctx.fillStyle = '#555';
  ctx.fillRect(-r / 1.4, -r / 1.4, r * 1.4, r * 1.4);
  ctx.strokeStyle = 'rgba(0,0,0,0.3)';
  ctx.lineWidth = 0.5;
  ctx.strokeRect(-r / 1.4, -r / 1.4, r * 1.4, r * 1.4);
  ctx.restore();
}

/* ══════════════════ Lane path drawing ══════════════════ */
const LANE_COLORS: Record<Lane, string> = {
  top: 'rgba(255, 200, 50, 0.45)',
  mid: 'rgba(180, 130, 255, 0.45)',
  bot: 'rgba(50, 220, 180, 0.45)',
};

function drawLanePaths(
  ctx: CanvasRenderingContext2D,
  lanePaths: Record<Lane, LanePath> | null,
): void {
  if (!lanePaths) return;
  for (const lane of ['top', 'mid', 'bot'] as Lane[]) {
    const path = lanePaths[lane];
    if (!path || path.waypoints.length < 2) continue;
    ctx.beginPath();
    ctx.moveTo(path.waypoints[0].x, path.waypoints[0].y);
    for (let i = 1; i < path.waypoints.length; i++) {
      ctx.lineTo(path.waypoints[i].x, path.waypoints[i].y);
    }
    ctx.strokeStyle = LANE_COLORS[lane];
    ctx.lineWidth = 2;
    ctx.stroke();

    // Small dots at waypoints
    for (const wp of path.waypoints) {
      ctx.beginPath();
      ctx.arc(wp.x, wp.y, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = LANE_COLORS[lane];
      ctx.fill();
    }
  }
}

/* ══════════════════ Role letter mapping ══════════════════ */

const ROLE_LETTER: Record<string, string> = {
  top: 'T',
  jungle: 'J',
  mid: 'M',
  adc: 'A',
  support: 'S',
};

/* ══════════════════ Component ══════════════════ */

export interface SimulatorMapHandle {
  /** Render minions and towers onto the canvas (called from rAF) */
  renderMinions: (minions: Minion[], towers?: Tower[]) => void;
  /** Reset zoom & pan */
  resetView: () => void;
}

interface SimulatorMapProps {
  /** Lane paths to draw as faint guide lines */
  lanePaths: Record<Lane, LanePath> | null;
  /** Jungle camps to render as HTML overlays */
  camps: JungleCamp[];
  /** Current game time in seconds */
  gameTime: number;
  /** Player-controlled champion markers */
  champions: SimChampion[];
  /** Currently-selected champion id (null = none) */
  selectedChampionId: number | null;
  /** Called when a champion is (de)selected */
  onSelectChampion: (id: number | null) => void;
  /** Called when a camp overlay is clicked. gx/gy = grid coords of the click point. */
  onCampClick?: (campId: string, clickGx?: number, clickGy?: number) => void;
  /** Called when the map is right-clicked (grid coords) */
  onMapRightClick?: (gx: number, gy: number) => void;
  /** Extra class names */
  className?: string;
  /* ── Jungle Mode extras ── */
  /** When true, allow clicking dead camps and show level badges */
  jungleModeActive?: boolean;
  /** Champion id → level (shown next to role letter) */
  levelBadges?: Record<number, number>;
  /** Camp id currently being cleared (triggers pulse animation) */
  clearingCampId?: string | null;
  /** Floating gold text popup */
  goldPopup?: { gx: number; gy: number; text: string } | null;
  /** Champion id currently recalling (triggers swirl animation) */
  recallingChampId?: number | null;
  /** Camp levels to display (campId → level) */
  campLevelBadges?: Record<string, number>;
}

const SimulatorMap = forwardRef<SimulatorMapHandle, SimulatorMapProps>(
  (
    {
      lanePaths,
      camps,
      gameTime,
      champions,
      selectedChampionId,
      onSelectChampion,
      onCampClick,
      onMapRightClick,
      className,
      jungleModeActive,
      levelBadges,
      clearingCampId,
      goldPopup,
      recallingChampId,
      campLevelBadges,
    },
    ref,
  ) => {
    /* ── Zoom / pan state ── */
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [spaceHeld, setSpaceHeld] = useState(false);

    const viewportRef = useRef<HTMLDivElement>(null);
    const laneCanvasRef = useRef<HTMLCanvasElement>(null);
    const minionCanvasRef = useRef<HTMLCanvasElement>(null);
    const panningRef = useRef(false);
    const lastPanRef = useRef({ x: 0, y: 0 });
    const spaceRef = useRef(false);
    const sRef = useRef({ zoom, pan });

    useEffect(() => {
      sRef.current = { zoom, pan };
    });

    /* ── Derived: selected champion object ── */
    const selectedChamp =
      champions.find((c) => c.id === selectedChampionId) ?? null;

    /* ── Clamp pan helper ── */
    const clampPan = useCallback(
      (px: number, py: number, z: number) => {
        const vp = viewportRef.current;
        if (!vp) return { x: px, y: py };
        const vw = vp.clientWidth;
        const vh = vp.clientHeight;
        const sw = vw * z;
        const sh = vh * z;
        if (z <= 1) return { x: 0, y: 0 };
        return {
          x: Math.min(0, Math.max(vw - sw, px)),
          y: Math.min(0, Math.max(vh - sh, py)),
        };
      },
      [],
    );

    /* ── Draw lane paths when they change ── */
    useEffect(() => {
      const canvas = laneCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, GRID_RES, GRID_RES);
      drawLanePaths(ctx, lanePaths);
    }, [lanePaths]);

    /* ── Imperative handle ── */
    useImperativeHandle(ref, () => ({
      renderMinions(minions: Minion[], towers?: Tower[]) {
        const canvas = minionCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, GRID_RES, GRID_RES);

        // Draw towers first (underneath minions)
        if (towers) {
          for (const t of towers) {
            if (!t.alive) drawDestroyedTower(ctx, t);
          }
          for (const t of towers) {
            if (t.alive) drawTower(ctx, t, true);
          }
        }

        // Draw minions on top
        for (const m of minions) drawMinion(ctx, m);
      },
      resetView() {
        setZoom(1);
        setPan({ x: 0, y: 0 });
      },
    }));

    /* ── Pointer handlers for panning ── */
    function onPointerDown(e: React.PointerEvent) {
      if (e.button === 1 || (e.button === 0 && spaceRef.current)) {
        panningRef.current = true;
        lastPanRef.current = { x: e.clientX, y: e.clientY };
        viewportRef.current?.setPointerCapture(e.pointerId);
      }
    }
    function onPointerMove(e: React.PointerEvent) {
      if (!panningRef.current) return;
      const dx = e.clientX - lastPanRef.current.x;
      const dy = e.clientY - lastPanRef.current.y;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      setPan((p) => clampPan(p.x + dx, p.y + dy, sRef.current.zoom));
    }
    function onPointerUp(e: React.PointerEvent) {
      if (panningRef.current) {
        panningRef.current = false;
        viewportRef.current?.releasePointerCapture(e.pointerId);
      }
    }

    /* ── Scroll zoom ── */
    useEffect(() => {
      const vp = viewportRef.current;
      if (!vp) return;
      function onWheel(e: WheelEvent) {
        e.preventDefault();
        const s = sRef.current;
        const rect = vp!.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const dir = e.deltaY < 0 ? 1 : -1;
        let nz = dir > 0 ? s.zoom * ZOOM_STEP : s.zoom / ZOOM_STEP;
        nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nz));

        const scale = nz / s.zoom;
        const npx = mx - (mx - s.pan.x) * scale;
        const npy = my - (my - s.pan.y) * scale;

        setZoom(nz);
        setPan(
          nz <= 1 ? { x: 0, y: 0 } : clampPan(npx, npy, nz),
        );
      }
      vp.addEventListener('wheel', onWheel, { passive: false });
      return () => vp.removeEventListener('wheel', onWheel);
    }, [clampPan]);

    /* ── Prevent middle-click browser scroll ── */
    useEffect(() => {
      const vp = viewportRef.current;
      if (!vp) return;
      const prevent = (e: MouseEvent) => {
        if (e.button === 1) e.preventDefault();
      };
      vp.addEventListener('mousedown', prevent);
      return () => vp.removeEventListener('mousedown', prevent);
    }, []);

    /* ── Keyboard (space for pan) ── */
    useEffect(() => {
      function down(e: KeyboardEvent) {
        if (e.code === 'Space' && !spaceRef.current) {
          spaceRef.current = true;
          setSpaceHeld(true);
        }
      }
      function up(e: KeyboardEvent) {
        if (e.code === 'Space') {
          spaceRef.current = false;
          setSpaceHeld(false);
        }
      }
      window.addEventListener('keydown', down);
      window.addEventListener('keyup', up);
      return () => {
        window.removeEventListener('keydown', down);
        window.removeEventListener('keyup', up);
      };
    }, []);

    /* ── Render ── */
    return (
      <div
        ref={viewportRef}
        className={`sim-viewport${spaceHeld ? ' pan-mode' : ''} ${className ?? ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!onMapRightClick) return;
          const rect = viewportRef.current?.getBoundingClientRect();
          if (!rect) return;
          const relX = e.clientX - rect.left;
          const relY = e.clientY - rect.top;
          const s = sRef.current;
          const stageX = (relX - s.pan.x) / s.zoom;
          const stageY = (relY - s.pan.y) / s.zoom;
          const gx = Math.round((stageX / rect.width) * GRID_RES);
          const gy = Math.round((stageY / rect.height) * GRID_RES);
          if (gx >= 0 && gx < GRID_RES && gy >= 0 && gy < GRID_RES) {
            onMapRightClick(gx, gy);
          }
        }}
      >
        <div
          className="sim-stage"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onSelectChampion(null);
          }}
        >
          <img
            className="sim-map"
            src="/Images/riftmap.jpg"
            alt="Summoner's Rift"
            draggable={false}
          />
          <canvas
            ref={laneCanvasRef}
            className="sim-lane-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />

          {/* ── Camp overlays (HTML for crisp text) ── */}
          {camps.map((camp) => {
            const timerSec =
              !camp.alive && camp.nextSpawnAt < Infinity
                ? Math.max(0, Math.ceil(camp.nextSpawnAt - gameTime))
                : 0;
            const timerLabel =
              timerSec > 0
                ? timerSec >= 60
                  ? `${Math.floor(timerSec / 60)}:${(timerSec % 60)
                      .toString()
                      .padStart(2, '0')}`
                  : `${timerSec}s`
                : '';

            let travelLabel = '';
            if (selectedChamp && camp.alive) {
              const dx = selectedChamp.gx - camp.gx;
              const dy = selectedChamp.gy - camp.gy;
              const distG = Math.sqrt(dx * dx + dy * dy);
              const sec = Math.round(
                (distG * CELL_SIZE) / selectedChamp.moveSpeed,
              );
              travelLabel = `${sec}s`;
            }

            return (
              <div
                key={camp.id}
                className={`sim-camp-overlay${camp.alive ? '' : ' sim-camp-dead'}${clearingCampId === camp.id ? ' sim-camp-clearing' : ''}`}
                style={{
                  left: `${(camp.gx / GRID_RES) * 100}%`,
                  top: `${(camp.gy / GRID_RES) * 100}%`,
                  cursor: (!camp.alive && !jungleModeActive) ? 'default' : 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!camp.alive && !jungleModeActive) return;

                  // Convert click position to grid coordinates
                  const vp = viewportRef.current;
                  if (vp && onCampClick) {
                    const rect = vp.getBoundingClientRect();
                    const s = sRef.current;
                    const relX = e.clientX - rect.left;
                    const relY = e.clientY - rect.top;
                    const stageX = (relX - s.pan.x) / s.zoom;
                    const stageY = (relY - s.pan.y) / s.zoom;
                    const gx = Math.round((stageX / rect.width) * GRID_RES);
                    const gy = Math.round((stageY / rect.height) * GRID_RES);
                    onCampClick(camp.id, gx, gy);
                  } else {
                    onCampClick?.(camp.id);
                  }
                }}
              >
                {/* Leash range circle — clickable area */}
                <div
                  className={`sim-camp-leash${camp.alive ? ' sim-camp-leash-alive' : ''}`}
                  style={{
                    width: `${(CAMP_LEASH_PCT / 2.8) * 100}%`,
                    paddingBottom: `${(CAMP_LEASH_PCT / 2.8) * 100}%`,
                  }}
                />
                <img
                  src={campImageUrl(camp.imageKey)}
                  alt={camp.name}
                  className="sim-camp-img"
                  draggable={false}
                />
                {timerLabel && (
                  <span className="sim-camp-timer">{timerLabel}</span>
                )}
                {travelLabel && (
                  <span className="sim-camp-travel">{travelLabel}</span>
                )}
                {campLevelBadges?.[camp.id] != null && campLevelBadges[camp.id] > 1 && (
                  <span className="sim-camp-level-badge">
                    Lv{campLevelBadges[camp.id]}
                  </span>
                )}
              </div>
            );
          })}

          <canvas
            ref={minionCanvasRef}
            className="sim-minion-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />

          {/* ── Champion overlays ── */}
          {champions.map((ch) => {
            const level = levelBadges?.[ch.id];
            return (
              <div
                key={ch.id}
                className={`sim-champ-node sim-champ-${ch.team}${
                  selectedChampionId === ch.id ? ' sim-champ-selected' : ''
                }${recallingChampId === ch.id ? ' sim-champ-recalling' : ''}`}
                style={{
                  left: `${(ch.gx / GRID_RES) * 100}%`,
                  top: `${(ch.gy / GRID_RES) * 100}%`,
                  // In jungle mode, let clicks pass through to camp leash circles below
                  pointerEvents: jungleModeActive ? 'none' : 'auto',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectChampion(ch.id);
                }}
              >
                <span className="sim-champ-letter">
                  {ROLE_LETTER[ch.role] ?? '?'}
                </span>
                {level != null && (
                  <span className="sim-champ-level">{level}</span>
                )}
              </div>
            );
          })}
          {/* ── Gold popup (jungle mode) ── */}
          {goldPopup && (
            <div
              className="sim-gold-popup"
              key={`gold-${goldPopup.gx}-${goldPopup.gy}-${Date.now()}`}
              style={{
                left: `${(goldPopup.gx / GRID_RES) * 100}%`,
                top: `${(goldPopup.gy / GRID_RES) * 100}%`,
              }}
            >
              {goldPopup.text}
            </div>
          )}
        </div>
      </div>
    );
  },
);

SimulatorMap.displayName = 'SimulatorMap';
export default SimulatorMap;
